import type { ReactNode } from 'react';

interface Props {
  title: string;
  /**
   * How much is inside. Shown beside the title so that a shut fold still says
   * whether opening it is worth the click — a card of shut sections that all
   * look alike costs more clicks than the sections saved.
   *
   * `null` while the count is still being fetched, and nothing is shown: an
   * absent badge that later turns into "3" is honest, a "0" that does the same
   * is a lie held for half a second.
   */
  count?: number | null;
  /** Open from the start, for a section that is the reason the card was opened. */
  open?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A section of a card that starts shut.
 *
 * Built on `<details>` rather than on a state variable: the browser gives the
 * keyboard, the screen reader and the browser's own find-in-page for free, and
 * find-in-page is the one that matters — Ctrl+F reaches text inside a shut
 * `<details>`, and would never have reached a branch React declined to render.
 *
 * Which is also why the contents stay mounted while shut. They cost their data
 * either way, and the alternative — mount on open — would mean every fold
 * flashes a spinner at the moment it is opened, and could not be counted before
 * it was opened at all.
 */
export function Fold({ title, count = null, open = false, className, children }: Props) {
  return (
    <details className={className ? `fold ${className}` : 'fold'} open={open}>
      <summary className="fold__head">
        <h3>{title}</h3>
        {count !== null && count > 0 && <span className="fold__count">{count}</span>}
      </summary>
      <div className="fold__body">{children}</div>
    </details>
  );
}
